/**
 * CopyButton — bottone "copia negli appunti" con la classica icona dei due quadrati sovrapposti.
 *
 * Il testo da copiare può essere noto subito (`text`) o letto solo al click (`getText`, es. un
 * segreto che non deve restare nella pagina): in quel caso il valore NON viene mai renderizzato.
 * Dopo la copia il bottone mostra per qualche secondo l'esito ("Copiato" / errore).
 */

import { useEffect, useState } from 'react';
import styles from './CopyButton.module.css';

/**
 * Copia `text` negli appunti. Usa la Clipboard API (contesti sicuri: https/localhost) e ripiega
 * su `document.execCommand('copy')` con una textarea temporanea (http, browser datati).
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  try {
    if (!document.execCommand('copy')) throw new Error('Copia non supportata dal browser');
  } finally {
    document.body.removeChild(area);
  }
}

/** Icona "copia": due quadrati sovrapposti. */
export function CopyIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect
        x="5.5"
        y="5.5"
        width="8.5"
        height="8.5"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M10.5 3.5V3a1.5 1.5 0 0 0-1.5-1.5H3.5A1.5 1.5 0 0 0 2 3v5.5A1.5 1.5 0 0 0 3.5 10H4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

type Esito = 'idle' | 'copied' | 'error';

/** Durata del feedback "Copiato" prima di tornare allo stato iniziale. */
const FEEDBACK_MS = 2000;

export interface CopyButtonProps {
  /** Testo da copiare, noto al render. */
  text?: string;
  /** Alternativa a `text`: il valore è letto solo al click (es. un segreto). */
  getText?: () => Promise<string>;
  /** Etichetta accessibile e tooltip (es. "Copia URL"). */
  label: string;
  testId: string;
  disabled?: boolean;
  /** Mostra anche l'etichetta testuale accanto all'icona. */
  showLabel?: boolean;
}

export function CopyButton({
  text,
  getText,
  label,
  testId,
  disabled,
  showLabel = false,
}: CopyButtonProps) {
  const [esito, setEsito] = useState<Esito>('idle');
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    if (esito === 'idle') return;
    const t = setTimeout(() => setEsito('idle'), FEEDBACK_MS);
    return () => clearTimeout(t);
  }, [esito]);

  async function handleClick(e: React.MouseEvent) {
    // Il bottone vive anche dentro righe cliccabili (es. ticket): niente toggle della riga.
    e.stopPropagation();
    setInCorso(true);
    try {
      const valore = getText ? await getText() : (text ?? '');
      await copyToClipboard(valore);
      setEsito('copied');
    } catch {
      setEsito('error');
    } finally {
      setInCorso(false);
    }
  }

  const titolo =
    esito === 'copied' ? 'Copiato negli appunti' : esito === 'error' ? 'Copia non riuscita' : label;

  return (
    <button
      type="button"
      className={styles.copyBtn}
      onClick={(e) => {
        void handleClick(e);
      }}
      disabled={disabled || inCorso}
      aria-label={label}
      title={titolo}
      data-testid={testId}
      data-esito={esito}
    >
      <CopyIcon />
      {showLabel && <span>{label}</span>}
      {esito === 'copied' && (
        <span className={styles.feedbackOk} role="status">
          Copiato
        </span>
      )}
      {esito === 'error' && (
        <span className={styles.feedbackErr} role="alert">
          Errore
        </span>
      )}
    </button>
  );
}
